import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { Box, Button, InputAdornment, TextField } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockIcon from '@mui/icons-material/Lock';
import LoginIcon from '@mui/icons-material/Login';
import { toastApiError } from '../../utils/ApiError';
import { login } from '../../services/Auth';
import { getTokenDataFromStoredToken, storeAuthToken } from '../../utils/Auth';
import { tokenDataAtom } from '../../atoms/TokenData';
import Waiting from '../Waiting';
import { sharedColors, sharedStyles } from '../../utils/Style';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setTokenData = useSetRecoilState(tokenDataAtom);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setLoading(true);

    login(username, password)
      .then((loginResponse) => {
        storeAuthToken(loginResponse.accessToken);

        const tokenData = getTokenDataFromStoredToken();
        if (tokenData) {
          setTokenData(tokenData);
        }

        navigate('/', { replace: true });
      })
      .catch((err) => toastApiError(err))
      .finally(() => setLoading(false));
  };

  return (
    <Box
      component='main'
      sx={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: sharedColors.gray1,
        px: 2,
        py: 4,
      }}
    >
      <Waiting open={loading} />
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          maxWidth: 360,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 3,
          backgroundColor: sharedColors.white,
          border: `1px solid ${sharedColors.gray8}`,
          borderRadius: 1,
        }}
      >
        <Box
          component='img'
          src='/nobetio.png'
          alt='nöbetio'
          sx={{ width: 120, mx: 'auto', mb: 1 }}
        />

        <TextField
          label={t('login.username')}
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          size='small'
          autoComplete='username'
          inputProps={{
            autoCapitalize: 'none',
            autoCorrect: 'off',
            spellCheck: false,
          }}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <AccountCircleIcon color='action' />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label={t('login.password')}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          size='small'
          type='password'
          autoComplete='current-password'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <LockIcon color='action' />
              </InputAdornment>
            ),
          }}
        />
        <Button
          type='submit'
          variant='contained'
          startIcon={<LoginIcon />}
          disabled={!username || !password || loading}
          sx={{ ...sharedStyles.buttonText, mt: 1 }}
        >
          {t('login.submit')}
        </Button>
      </Box>
    </Box>
  );
};

export default LoginPage;
